/**
 * Перенос `presentation/providers/chat_controller/chat_duration_controller.dart`.
 *
 * Отсчёт бездействия в чате: пока посетитель молчит `totalDuration`,
 * экран возвращается к выбору языка (`onTimeExceed`). `Duration` переносится
 * числом миллисекунд — как и везде в порте.
 */

import { CustomValueNotifier } from '@/utils/customValueNotifier';
import { Nullable, resolveNullable } from '@/utils/nullable';

/** `Duration(seconds: 1)` — шаг таймера по умолчанию. */
const DEFAULT_TICK_UPDATE_DURATION_MS = 1000;

/** `Duration(minutes: 5)` — сколько всего ждём посетителя. */
const DEFAULT_TOTAL_DURATION_MS = 5 * 60 * 1000;

/** `Duration.zero` — ниже нуля остаток не опускается. */
const ZERO_DURATION_MS = 0;

/** Аргументы конструктора [ChatDurationState]. */
export interface ChatDurationStateInit {
  readonly tickDuration?: number | null;
  readonly totalDuration: number;
}

/** Патч [ChatDurationState.copyWith]; `tickDuration` обнуляется `Nullable`. */
export interface ChatDurationStatePatch {
  readonly tickDuration?: Nullable<number | null>;
  readonly totalDuration?: number;
}

export class ChatDurationState {
  constructor(init: ChatDurationStateInit) {
    this.tickDuration = init.tickDuration ?? null;
    this.totalDuration = init.totalDuration;
  }

  /** Сколько уже прошло; `null` — таймер стоит. */
  readonly tickDuration: number | null;

  readonly totalDuration: number;

  /** `Duration? get leftDuration` — остаток, не меньше нуля. */
  get leftDuration(): number | null {
    const { tickDuration } = this;
    if (tickDuration === null) {
      return null;
    }
    const duration = this.totalDuration - tickDuration;
    if (duration < ZERO_DURATION_MS) {
      return ZERO_DURATION_MS;
    }
    return duration;
  }

  copyWith(patch: ChatDurationStatePatch): ChatDurationState {
    return new ChatDurationState({
      tickDuration: resolveNullable(patch.tickDuration, this.tickDuration),
      totalDuration: patch.totalDuration ?? this.totalDuration,
    });
  }
}

/** Аргументы конструктора [ChatDurationController]. */
export interface ChatDurationControllerInit {
  readonly autoStart?: boolean;
  readonly tickUpdateDuration?: number;
  readonly totalDuration?: number;
  readonly onTimeExceed?: (() => void) | null;
}

export class ChatDurationController extends CustomValueNotifier<ChatDurationState> {
  constructor(init: ChatDurationControllerInit = {}) {
    super();
    this.autoStart = init.autoStart ?? true;
    this.tickUpdateDuration = init.tickUpdateDuration ?? DEFAULT_TICK_UPDATE_DURATION_MS;
    this.totalDuration = init.totalDuration ?? DEFAULT_TOTAL_DURATION_MS;
    this.onTimeExceed = init.onTimeExceed ?? null;
    // Базовый класс не может построить состояние сам: на момент его
    // конструктора поля наследника ещё не присвоены.
    this.initialize();
  }

  readonly totalDuration: number;
  readonly tickUpdateDuration: number;
  readonly onTimeExceed: (() => void) | null;
  readonly autoStart: boolean;

  #timer: ReturnType<typeof setInterval> | null = null;

  /**
   * `timer.tick` из `Timer.periodic`: у `setInterval` своего счётчика нет,
   * поэтому номер срабатывания считается вручную — 1, 2, 3, … Умножение на
   * `tickUpdateDuration` даёт то же самое пройденное время, что и в Dart,
   * и не накапливает дрейф, как сложение шагов.
   */
  #tick = 0;

  protected override buildState(): ChatDurationState {
    return new ChatDurationState({
      tickDuration: null,
      totalDuration: this.totalDuration,
    });
  }

  override dispose(): void {
    this.stopTimer();
    super.dispose();
  }

  protected override initState(): void {
    super.initState();
    if (this.autoStart) {
      this.startTimer();
    }
  }

  startTimer(): void {
    if (this.isDisposed) return;
    // `_timer ??= Timer.periodic(...)` — уже запущенный таймер не трогаем.
    if (this.#timer !== null) return;
    this.#tick = 0;
    this.#timer = setInterval(() => {
      this.#onTick();
    }, this.tickUpdateDuration);
  }

  stopTimer(): void {
    if (this.#timer !== null) clearInterval(this.#timer);
    this.#timer = null;
    this.#tick = 0;
    this.updateValue((old) => old.copyWith({ tickDuration: new Nullable(null) }));
  }

  restartTimer(): void {
    this.stopTimer();
    this.startTimer();
  }

  /** Тело колбэка `Timer.periodic`. */
  #onTick(): void {
    this.#tick += 1;
    const tickDuration = this.tickUpdateDuration * this.#tick;
    this.updateValue((old) => old.copyWith({ tickDuration: new Nullable(tickDuration) }));
    if (tickDuration >= this.value.totalDuration) {
      this.stopTimer();
      this.onTimeExceed?.();
    }
  }
}
